// Fleet role system — Cognito group → permission mapping + static session metadata.
import { getRuntimeConfig } from "../types/RuntimeConfig";

// #2542 — correct fallback: fleet routes live on api.2hives.ai (CloudFront →
// c47ru51ngd TwoBeeFleet HTTP API), NOT on 99xf040rkb (admin REST API).
// #2574 — WS fallback was wss://api.2hives.ai (wrong host); canonical WS
// edge is wss://ws.2hives.ai (matches runtime-config envelope +
// WS_BASE_URL env on http_runtime_config.py / FleetApiStack). Pre-runtime-
// config-hydration WS connections were landing on the wrong host and the
// browser silently dropped them — now they hit the right edge even before
// /v1/runtime-config resolves.
const FALLBACK_FLEET_HTTP_URL = "https://api.2hives.ai";
const FALLBACK_FLEET_WS_URL   = "wss://ws.2hives.ai";

// #C — was VITE_FLEET_HTTP_URL / hardcoded wss host. Now read from the
// runtime-config envelope hydrated by <ConfigProvider> (sub-card #B) into
// window.__2HIVES_CONFIG__.
//
// Lazy getters so each call site picks up post-hot-reload updates.
// #2546 — removed the eager FLEET_HTTP_URL / FLEET_WS_URL module-eval-time
// constants; all call sites now invoke the getter directly so the bundle
// never embeds a raw API GW URL.
export function getFleetHttpUrl(): string {
  return getRuntimeConfig()?.apiBaseUrl ?? FALLBACK_FLEET_HTTP_URL;
}

export function getFleetWsUrl(): string {
  return getRuntimeConfig()?.wsBaseUrl ?? FALLBACK_FLEET_WS_URL;
}
// Fleet groups: fleet-admin | fleet-dispatcher | fleet-viewer | pmo (viewer-level).
// New Cognito groups (fleet-dispatcher, fleet-viewer) pending backend creation.

export type FleetRole = "fleet-admin" | "fleet-dispatcher" | "fleet-viewer" | "pmo" | null;
type BoxName   = "local" | "cloud";
export type Tier      = "core" | "ondemand";
// Raw model ID from the backend — use getCapabilityTier() before rendering to customers.
type ModelName = string;

export interface FleetClaims {
  role:        string | null;
  groups:      string[];
  email:       string | null;
  projects:    string[];  // empty = all projects allowed (no restriction)
  tenant_id:   string | null;
  tenant_role: "owner" | "admin" | "member" | null;
}

export function parseFleetClaims(token: string | null): FleetClaims {
  if (!token) return { role: null, groups: [], email: null, projects: [], tenant_id: null, tenant_role: null };
  try {
    const p = JSON.parse(atob(token.split(".")[1]));
    const projectsRaw = p["custom:projects"];
    const projects: string[] = projectsRaw
      ? String(projectsRaw).split(",").map((s: string) => s.trim()).filter(Boolean)
      : [];
    const rawTenantRole = p["custom:tenant_role"];
    const tenant_role: FleetClaims["tenant_role"] =
      rawTenantRole === "owner" || rawTenantRole === "admin" || rawTenantRole === "member"
        ? rawTenantRole : null;
    return {
      role:        p["custom:role"] ?? null,
      groups:      Array.isArray(p["cognito:groups"]) ? p["cognito:groups"] : [],
      email:       p.email ?? p["cognito:username"] ?? null,
      projects,
      tenant_id:   p["custom:tenant_id"] ?? null,
      tenant_role,
    };
  } catch { return { role: null, groups: [], email: null, projects: [], tenant_id: null, tenant_role: null }; }
}

export function getFleetRole(claims: FleetClaims): FleetRole {
  const g = claims.groups;
  const r = claims.role;
  // Accept both the short alias ("admin") and the canonical DDB value ("fleet-admin")
  if (g.includes("fleet-admin") || r === "fleet-admin" || r === "admin") return "fleet-admin";
  if (g.includes("fleet-dispatcher") || r === "fleet-dispatcher")         return "fleet-dispatcher";
  if (g.includes("fleet-viewer")     || r === "fleet-viewer")             return "fleet-viewer";
  if (g.includes("pmo")              || r === "pmo")                      return "pmo";
  return null;
}

export function isAmbassador(claims: FleetClaims):     boolean { return claims.groups.includes("ambassador") || claims.role === "ambassador"; }
export function isOrgAdmin(claims: FleetClaims):       boolean { return claims.groups.includes("org_admin") || claims.role === "org_admin"; }
export function canViewFleet(claims: FleetClaims):     boolean { return getFleetRole(claims) !== null; }
export function canDispatch(claims: FleetClaims):      boolean {
  const r = getFleetRole(claims);
  return r === "fleet-admin" || r === "fleet-dispatcher";
}
// Platform admin — node/user/project management. fleet-admin only.
export function canAdminPlatform(claims: FleetClaims): boolean { return getFleetRole(claims) === "fleet-admin"; }

export function hasTenant(claims: FleetClaims): boolean { return claims.tenant_id != null; }
export function canAdminTenant(claims: FleetClaims): boolean {
  return claims.tenant_role === "owner" || claims.tenant_role === "admin";
}

export function roleLabel(role: FleetRole): string {
  return role ?? "no access";
}

// ── Session metadata ──────────────────────────────────────────────────────────
// Populated entirely from backend per tenant — no hardcoded sessions.
// SessionMetaContext fetches GET /fleet/sessions/metadata per project on login.

export interface SessionMeta {
  name:    string;
  box:     BoxName;
  tier:    Tier;
  model:   ModelName;
  dir:     string;
  project: string;
}

// Empty — all session data comes from the backend per authenticated tenant.
export const SESSION_META: Record<string, SessionMeta> = {};

// Empty — role descriptions come from the backend.
export const SESSION_ROLES: Record<string, string> = {};

// Empty — session list comes from backend.
export const KNOWN_SESSIONS: string[] = [];

// Returns the SESSION_META lookup key for a (target, project) pair.
// Compound keys "project/session" for all named projects; bare key when project is absent.
export function sessionKey(target: string, project?: string | null): string {
  if (!project) return target;
  return `${project}/${target}`;
}

// Extracts the real tmux session name from a compound or bare key.
export function sessionRealName(key: string): string {
  return key.includes("/") ? key.split("/").slice(1).join("/") : key;
}

// Empty — projects come from backend per tenant.
export const KNOWN_PROJECTS: string[] = [];

// Empty — colors are assigned dynamically from tenant project list.
export const PROJECT_COLORS: Record<string, string> = {};

// Empty — prefixes are derived dynamically.
export const PROJECT_PREFIX_MAP: Record<string, string> = {};

// Returns a display label for a session: "project-session" when project is set, else just session name.
export function displaySessionName(project: string | null | undefined, session: string): string {
  if (!project) return session;
  return `${project}-${session}`;
}

// #2851 Wave 2 — per-host identity colours routed through brand tokens so
// light/dark themes stay coherent. Hetzner → info (blue), jamesave →
// success (green), Gergos-Mac-Pro → sent/purple. CSS vars resolve at paint
// time, so consumers must read these in a render context.
export const HOST_COLORS: Record<string, string> = {
  "hetzner":        "var(--brand-status-info)",
  "jamesave":       "var(--brand-status-success)",
  "Gergos-Mac-Pro": "var(--brand-job-sent)",
};

// ── Sample prompts — per-agent-role cold-start affordance (#2320) ─────────────
// Keyed by the agent's `role` field (lower-case). Shown as clickable suggestion
// cards when `messages.length === 0`. Fallback ("*") used for unknown roles.
export const ROLE_SAMPLE_PROMPTS: Record<string, string[]> = {
  // Internal fleet roles
  ceo: [
    "What should I focus on this week?",
    "Show me today's progress",
    "I have a decision I need help with",
    "Surface any blockers across the team",
  ],
  pmo: [
    "Show me overdue cards",
    "What's the current backlog count?",
    "Surface stale cards not touched in 7 days",
    "List all P0 and P1 open items",
  ],
  backend: [
    "Write a Lambda function for user signup",
    "Review my code and suggest improvements",
    "Run the tests and show me any failures",
    "Help me design a DynamoDB data model",
  ],
  frontend: [
    "Build a React component for a data table",
    "Review the new page for accessibility issues",
    "Run TSC + tests and fix any errors",
    "Help me add dark mode to this component",
  ],
  architect: [
    "Review our current system architecture",
    "Draft an architecture decision record",
    "Identify potential scalability bottlenecks",
    "Recommend a data storage strategy for this use case",
  ],
  sales: [
    "Draft a follow-up email for a cold prospect",
    "Help me prepare for an upcoming discovery call",
    "Summarise our pipeline by stage",
    "Write a competitive battle card",
  ],
  legal: [
    "Review this contract clause for risk",
    "Draft an NDA for a new vendor",
    "Summarise our compliance obligations",
    "Flag any IP issues in this agreement",
  ],
  finance: [
    "Summarise this month's cash flow",
    "Help me prepare a budget forecast",
    "Review our expense categories for anomalies",
    "Draft a financial update for the board",
  ],
  ops: [
    "What operational tasks are due this week?",
    "Help me write a runbook for server restarts",
    "Review our incident response process",
    "Summarise open supplier issues",
  ],
  marketing: [
    "Draft a social media post for our new feature",
    "Write a blog post outline about our product",
    "Review our current messaging for clarity",
    "Help me plan next month's content calendar",
  ],
  brand: [
    "Review this copy for brand voice alignment",
    "Help me write brand guidelines for a new product",
    "Draft naming options for a new feature",
    "Audit our visual consistency across channels",
  ],
  cyber: [
    "Run a threat model on this new API endpoint",
    "Review our current security posture",
    "Identify critical vulnerabilities in our stack",
    "Draft a security incident response plan",
  ],
  // Generic fallback
  "*": [
    "What can you help me with?",
    "Show me what you've been working on",
    "Help me get started on a task",
  ],
};

/** Returns sample prompts for a given agent role, falling back to generic prompts. */
export function getSamplePrompts(role?: string | null): string[] {
  if (!role) return ROLE_SAMPLE_PROMPTS["*"];
  const key = role.toLowerCase();
  return ROLE_SAMPLE_PROMPTS[key] ?? ROLE_SAMPLE_PROMPTS["*"];
}

// Dispatch presets — quick messages for common fleet ops.
export interface DispatchPreset {
  label:    string;
  icon:     string;
  message:  string;
  multiTarget: boolean;
}

export const DISPATCH_PRESETS: DispatchPreset[] = [
  {
    label:       "PING all",
    icon:        "📡",
    message:     "[PING from admin] liveness check — reply via fleet_session_send(name=\"admin\", message=\"[PONG from <your-session>] <one-line state>\")",
    multiTarget: true,
  },
  {
    label:       "STATUS check all",
    icon:        "📋",
    message:     "[from admin] status check — reply with your current task + any blockers",
    multiTarget: true,
  },
  {
    label:       "EOD summary",
    icon:        "🌙",
    message:     "[from admin] EOD: please report what you shipped today + anything that needs founder attention",
    multiTarget: true,
  },
  {
    label:       "Custom",
    icon:        "✏️",
    message:     "",
    multiTarget: false,
  },
];
